import { Card, Tooltip, useDrawer } from '@iveco-ti/iveco-ds'
import {
  LoaderCircle,
  OctagonAlert,
  Pencil,
  Pin,
  PinOff,
  Share2,
  Trash,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import PostDefaultImg from '@/assets/images/post-default.svg'
import { Toast } from '@/common/components/Toast'
import { ROUTES } from '@/common/constants/routes'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { PostModel } from '@/common/models/post'
import { useExcludePost } from '../hooks/useExcludePost'
import { useTogglePinPost } from '../hooks/useTogglePinPost'
import { useViewNews } from '../hooks/useViewNews'
import { useEditNewsStore } from '../stores/useEditNewsStore'

type NewsCardProps = {
  post: PostModel
}

export function NewsCard({ post }: NewsCardProps) {
  const { t } = useTranslation('panel')
  const { viewNews } = useViewNews()
  const { userInfo } = useGetUserInfo()
  const { handleTogglePinPost, isTogglingPinPost } = useTogglePinPost()
  const { handleExcludePost, isExcludingPost } = useExcludePost()
  const { setNews } = useEditNewsStore()
  const drawerEditNews = useDrawer('drawer-edit-news')

  const handleOpenDrawer = () => {
    viewNews(post)
  }

  const handleEditNews = () => {
    setNews(post)
    drawerEditNews.open()
  }

  const handleTogglePin = () => {
    handleTogglePinPost(post.id)
  }

  const handleCopySharableLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}${ROUTES.PANEL.NEWS}?id=${post.id}`,
      )

      toast(Toast, {
        data: {
          type: 'info',
          icon: Share2,
          description: t('news.card_news.copied_link_toast.success.title'),
        },
      })
    } catch {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          description: t('news.card_news.copied_link_toast.error.title'),
        },
      })
    }
  }

  const IS_PINNED = post?.PostsPinned?.length > 0

  return (
    <Card.Root>
      <Card.Body className="flex flex-col h-full min-h-36 relative">
        <div className="flex items-baseline justify-between">
          <p className="text-lg font-bold text-brand-900">{post?.title}</p>

          <div className="flex gap-2 items-center">
            {userInfo?.UserPreference?.allowedPosts && (
              <>
                <Tooltip text={t('news.card_news.delete_button.tooltip')}>
                  <button
                    type="button"
                    className="hover:bg-base-100 p-1 rounded"
                    onClick={() => handleExcludePost(post.id)}
                  >
                    {isExcludingPost ? (
                      <LoaderCircle className="size-4 text-danger-900 animate-spin" />
                    ) : (
                      <Trash className="size-4 text-danger-900" />
                    )}
                  </button>
                </Tooltip>

                <Tooltip text={t('news.card_news.edit_button.tooltip')}>
                  <button
                    type="button"
                    className="hover:bg-base-100 p-1 rounded"
                    onClick={handleEditNews}
                  >
                    <Pencil className="size-4 text-brand-900" />
                  </button>
                </Tooltip>
              </>
            )}

            <Tooltip
              text={
                IS_PINNED
                  ? t('news.card_news.unpin_button.tooltip')
                  : t('news.card_news.pin_button.tooltip')
              }
            >
              <button
                type="button"
                className="hover:bg-base-100 p-1 rounded"
                onClick={handleTogglePin}
              >
                {IS_PINNED && !isTogglingPinPost && (
                  <PinOff className="size-4 text-brand-900" />
                )}

                {!IS_PINNED && !isTogglingPinPost && (
                  <Pin className="size-4 text-brand-900" />
                )}

                {isTogglingPinPost && (
                  <LoaderCircle className="size-4 text-brand-900 animate-spin" />
                )}
              </button>
            </Tooltip>

            <Tooltip text={t('news.card_news.share_button.tooltip')}>
              <button
                type="button"
                className="hover:bg-base-100 p-1 rounded"
                onClick={handleCopySharableLink}
              >
                <Share2 className="size-4 text-brand-900" />
              </button>
            </Tooltip>
          </div>
        </div>

        <p className="text-base font-normal text-base-500">{post?.subTitle}</p>

        <div className="flex justify-between items-baseline gap-2 w-full mt-auto">
          <Card.Link
            className="cursor-pointer"
            text="Ver mais detalhes"
            onClick={handleOpenDrawer}
          />
        </div>
      </Card.Body>

      <Card.Image
        src={post.postImage || PostDefaultImg}
        alt={post.title || ''}
        className="h-36"
      />
    </Card.Root>
  )
}
